/**
 * ALCOHOL NAME MATCHING — single source of truth for "is this food an
 * alcoholic drink?" by name.
 *
 * MIRRORED BYTE-IDENTICAL to three places (pure TS, no imports):
 *   functions/src/utils/alcoholPatterns.ts  canonical — server Liver Score override
 *   utils/alcoholPatterns.ts                mobile display decisions
 *   webapp/src/lib/alcoholPatterns.ts       web display decisions
 * functions/test/alcoholPatterns.test.js fails if the copies drift. Edit the
 * canonical file, then copy it over the other two.
 *
 * Food names come back from the AI in the user's language, hence the
 * multilingual patterns across the app's 13 locales.
 */

/**
 * Alcoholic drink names. Any match (not excluded below) is ethanol — there is
 * no safe amount with liver disease, so the server floors the score at 90.
 */
export const ALCOHOL_PATTERNS: RegExp[] = [
  // English drink types + common cocktails
  /(?<!ginger\s)(?<!root\s)(?<!birch\s)(?<!butter\s)\bbeer\b/i, // "beer" yes; ginger/root/birch/butter beer (sodas) no
  /(?<!ginger\s)\bale\b/i, // "pale ale" yes, "ginger ale" (a soda) no
  /\blager\b/i,
  /\bstout\b/i,
  /\bwine\b/i,
  /\bchampagne\b/i,
  /\bprosecco\b/i,
  /\bvodka\b/i,
  /\bwhisk(e)?y\b/i,
  /\bbourbon\b/i,
  /\brum\b/i,
  /\btequila\b/i,
  /\bgin\b/i,
  /\bbrandy\b/i,
  /\bcognac\b/i,
  /\bliqueur\b/i,
  /\bliquor\b/i,
  /\bcocktail/i,
  /\bmargarita/i,
  /\bmojito/i,
  /\bsangria/i,
  /\bmimosa/i,
  /pi(ñ|n)a\s*colada/i,
  /hard\s*(seltzer|cider|lemonade|kombucha)/i, // "hard cider" yes; bare/"apple" cider (US, non-alc) no
  /white\s*claw/i, //             hard-seltzer brand
  // Named cocktails & aperitifs
  /\bnegroni\b/i,
  /\baperol\b/i,
  /\bcampari\b/i,
  /\bspritz\b(?!\s*(cookie|biscuit))/i, // Aperol/Hugo spritz; not "spritz cookies"
  /\bmartini\b/i,
  /\bmanhattan\b(?!\s+(clam|chowder))/i, // the cocktail; NOT Manhattan clam chowder
  /\bcosmopolitan\b/i,
  /\bdaiquiri\b/i, //             "virgin daiquiri" excluded by ALCOHOL_FREE_PATTERNS
  /\bmai\s*tai\b/i,
  /long\s*island\s+iced\s+tea/i, // NOT plain iced tea (protective)
  // Spirits, liqueurs & schnapps
  /\babsinthe\b/i,
  /\bgrappa\b/i,
  /\bouzo\b/i,
  /\blimoncello\b/i,
  /\bbailey'?s\b/i, //            Baileys Irish Cream
  /\bamaretto\b/i, //            liqueur; "amaretti" cookies end in -i, won't match
  /\bkahl(ú|u)a\b/i,
  /\bschnap{1,2}s\b/i,
  /\bvermouth\b/i,
  /\bmalt\s+(beverage|liquor)\b/i, // NOT malt vinegar / malted milk / malt loaf
  // Beers & fortified/dessert wines (guarded against non-alcohol homographs)
  /\bporter\b/i, //              dark beer; "porterhouse" steak keeps its boundary, won't match
  /\bmead\b/i, //                honey wine; "meadow" won't match (no boundary)
  /\bport\b(?!\s*salut)/i, //     port wine; NOT "Port Salut" cheese
  /(?<!cooking\s)\bsherry\b(?!\s*vinegar)/i, // drinking sherry; NOT cooking sherry / sherry vinegar
  /\bsake\b/i, //                 Japanese rice wine (romanized) — "sake" = salmon is excluded below
  /\bsoju\b/i, //                 Korean spirit (romanized)
  /\bcerveza/i, //                es beer
  /\bvino\b/i, //                 es/it wine
  /\bbi(è|e)re\b/i, //            fr beer
  /\bvin\b/i, //                  fr wine
  /\bbier\b/i, //                 de/nl beer
  /\bwein\b/i, //                 de wine
  /\bwijn\b/i, //                 nl wine
  /\bbirra\b/i, //                it beer
  /\bcerveja/i, //                pt beer
  /\bvinho\b/i, //                pt wine
  /\bpiwo\b/i, //                 pl beer
  /\bwino\b/i, //                 pl wine
  /\bbira\b/i, //                 tr beer
  /\bşarap\b/i, //                tr wine
  /ビール|ワイン|日本酒|焼酎|チューハイ/, //  ja beer/wine/sake/shochu/chuhai
  /맥주|소주|와인|막걸리/, //         ko beer/soju/wine/makgeolli
  /啤酒|葡萄酒|白酒|烧酒|燒酒/, //     zh beer/wine/baijiu/shaojiu
  /بيرة|نبيذ|خمر|ويسكي|فودكا/, //   ar beer/wine/liquor/whiskey/vodka
];

/**
 * Alcohol-free variants must NOT be treated as alcohol — "non-alcoholic beer"
 * is the exact swap the app recommends, so flagging it would punish users for
 * following our own advice. Checked before ALCOHOL_PATTERNS.
 */
export const ALCOHOL_FREE_PATTERNS: RegExp[] = [
  /non[\s-]?alcoholic/i,
  /alcohol[\s-]?free/i,
  /\bzero[\s-]?alcohol\b/i,
  /\b0([.,]0)?\s*%/, //           "0.0%" / "0,0%" labels
  /\bna\s+beer\b/i,
  /\bmocktail/i,
  /\bvirgin\s+(mojito|margarita|mary|pi(ñ|n)a|colada|daiquiri|sangria|mule|cocktail|mimosa|bellini)/i,
  /sin\s+alcohol/i, //            es
  /sans\s+alcool/i, //            fr
  /alkoholfrei/i, //              de
  /alcoholvrij/i, //              nl
  /analcolic[oa]/i, //            it
  /sem\s+([áa]lcool)/i, //        pt
  /bezalkoholow/i, //             pl
  /alkolsüz/i, //                 tr
  /ノンアルコール/, //               ja
  /무알코올|논알코올/, //             ko
  /无酒精|無酒精/, //                zh
  /خال[يٍ]?\s*من\s*الكحول/, //      ar
];

/**
 * Cooked off / not a drink: the alcohol word names a cooking method, a sauce,
 * a condiment, a savoury dish or a homograph. The prompt already tells the
 * model not to flag cooked-off alcohol, and neither the name match nor a stray
 * isAlcoholic:true may override it (a red 90 on salmon nigiri or a
 * vinaigrette salad is the fastest way to lose a user's trust).
 */
export const COOKED_OFF_PATTERNS: RegExp[] = [
  // Vinegars & dressings (wine / sherry / malt / rice-wine vinegar)
  /vinegar|vinaigrette|vinagre|vinaigre|essig|azijn|aceto\b|\bocet\b|\bsirke\b|ビネガー|식초|醋/i,
  // Cooking methods & sauces
  /\b(braised|braise|poached|simmered|stewed|marinated|marinade|glazed?|flamb(é|e)e?d?|deglazed|steamed|reduction|batter(ed)?|jus|gravy|risotto)\b/i,
  /beer[\s-]?batter/i,
  /sauce|soße|sosse|\bsalsa\b|\bmolho\b|\bsugo\b/i,
  /\bcooking\s+(wine|sherry|sake)\b/i,
  // Named savoury dishes
  /coq\s+au\s+vin|\bau\s+vin\b|bourguignon/i,
  /\balla\s+vodka\b|\bvodka\s+(pasta|penne|rigatoni)\b/i,
  /\bbourbon\s+(chicken|glazed?|bbq|barbecue|salmon|pecan|vanilla|steak|wings?)\b/i, // "bourbon vanilla" is a vanilla variety
  /\bbeer[\s-]?(bread|cheese|brats?|bratwurst|can\s+chicken|chicken|mustard)\b/i,
  /\bwine\s+gums?\b/i, //         candy, no alcohol
  // "sake" is also Japanese for salmon (sake nigiri / sashimi / maki / don)
  /\bsake\s+(nigiri|sashimi|maki|roll|don|onigiri|teriyaki|shioyaki|sushi|bowl|poke|salmon)\b/i,
  /\b(nigiri|sashimi|maki|onigiri|temaki|uramaki|chirashi|poke)\b.*\bsake\b/i,
  /\bsake\s*\(salmon\)/i,
];

/**
 * Baked goods & desserts: an alcohol word here is almost always baked off or
 * a flavour ("rum raisin ice cream", "stout gingerbread", "beer bread"), so the
 * NAME alone never flags them. The model's explicit isAlcoholic:true is still
 * honoured for these (a sherry trifle or a rum-soaked cake served un-cooked).
 */
export const DESSERT_PATTERNS: RegExp[] = [
  /\b(cake|cupcakes?|bread|cookies?|brownies?|pie|tart|gingerbread|pudding|ice\s*cream|gelato|sorbet|truffles?|fudge|muffins?|tiramisu|trifle|sabayon|zabaglione|custard)\b/i,
  /\brum\s+(raisin|balls?)\b/i,
];

/**
 * An explicit serving-of-a-drink phrase ("glass of", "pint of", "shot of")
 * means a drink is present even when the name also mentions a dish
 * ("steak and a glass of red wine") — the cooked-off / dessert exclusions
 * don't apply.
 */
export const DRINK_CONTEXT_PATTERNS: RegExp[] = [
  /\b(glass|glasses|pint|pints|bottle|bottles|can|cans|shot|shots|flute|pitcher|jug|carafe)\s+of\b/i,
];

export function isAlcoholFreeName(name: string): boolean {
  return ALCOHOL_FREE_PATTERNS.some((p) => p.test(name));
}

function hasDrinkContext(name: string): boolean {
  return DRINK_CONTEXT_PATTERNS.some((p) => p.test(name));
}

/** Cooked-off dish / sauce / condiment / homograph — never alcohol, even if the model flags it. */
export function isCookedOffName(name: string): boolean {
  if (hasDrinkContext(name)) return false;
  return COOKED_OFF_PATTERNS.some((p) => p.test(name));
}

/** Cooked-off dish or dessert — the NAME alone must not flag it as alcohol. */
export function isNotADrinkName(name: string): boolean {
  if (hasDrinkContext(name)) return false;
  return COOKED_OFF_PATTERNS.some((p) => p.test(name)) || DESSERT_PATTERNS.some((p) => p.test(name));
}

/** Name looks like an alcoholic drink (after the alcohol-free and not-a-drink exclusions). */
export function nameLooksAlcoholic(name: string | undefined | null): boolean {
  if (!name) return false;
  if (isAlcoholFreeName(name)) return false;
  if (isNotADrinkName(name)) return false;
  return ALCOHOL_PATTERNS.some((p) => p.test(name));
}
